import { twMerge } from 'tailwind-merge'
import { Skeleton } from '@renderer/components/ui/skeleton'

const ASSISTANT_WIDTHS = ['w-full', 'w-[92%]', 'w-[70%]'] as const

/** Mounting this many messages mid-slide drops frames; smaller chats go up immediately. */
const HEAVY_CHAT_DEFER_THRESHOLD = 10

/**
 * Whether a chat body is still waiting on its transcript: nothing cached for
 * this chat and the first fetch has not answered. Cached messages show at once
 * while the refetch runs behind them.
 */
export function isChatMessageListLoading({
  messageCount,
  isFetched,
}: {
  messageCount: number
  isFetched: boolean
}): boolean {
  return messageCount === 0 && !isFetched
}

/**
 * Whether the skeleton should hold the body: the transcript is still fetching,
 * or a large cached chat is waiting out the view's slide-in so markdown
 * mount does not drop those frames. Small cached chats skip the wait.
 */
export function shouldShowChatMessageListSkeleton({
  messageCount,
  isFetched,
  entranceSettled,
}: {
  messageCount: number
  isFetched: boolean
  entranceSettled: boolean
}): boolean {
  if (isChatMessageListLoading({ messageCount, isFetched })) {
    return true
  }
  return !entranceSettled && messageCount > HEAVY_CHAT_DEFER_THRESHOLD
}

interface ChatMessageListSkeletonProps {
  className?: string
}

/**
 * Placeholder for a chat transcript that is not on screen yet — either
 * messages have not loaded, or a large cached chat is waiting out the view's
 * slide-in so markdown mount does not drop those frames.
 */
export function ChatMessageListSkeleton({ className }: ChatMessageListSkeletonProps) {
  return (
    <div
      role="status"
      aria-label="Loading chat"
      className={twMerge('flex min-h-0 flex-1 flex-col gap-6 px-4 py-4', className)}
    >
      <AssistantLines />
      <UserBubble widthClassName="w-[42%]" />
      <AssistantLines />
      <UserBubble widthClassName="w-[55%]" />
      <AssistantLines />
    </div>
  )
}

function UserBubble({ widthClassName }: { widthClassName: string }) {
  return (
    <div className="flex justify-end" aria-hidden="true">
      <Skeleton className={twMerge('corner-smooth h-9 rounded-xl rounded-br-md', widthClassName)} />
    </div>
  )
}

function AssistantLines() {
  return (
    <div className="flex flex-col gap-2" aria-hidden="true">
      {ASSISTANT_WIDTHS.map((width) => (
        <Skeleton key={width} className={`h-3.5 ${width}`} />
      ))}
    </div>
  )
}
